import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import {
    ADD_EMAILS_TO_SUBSCRIPTION,
    DELETE_ALL_FOR_EMAIL,
    QUERY_ALL_COMMS,
    QUERY_CLIENT_EMAIL_SUBSCRIPTIONS,
    SYNC_EMAIL_COMMUNICATIONS,
} from './graphql';
import { normalizeEmails } from '@/utilities/validateEmail';
import {
    buildCommSections,
    diffVisibleCommunications,
    flattenCommSections,
    groupCommunicationsByEmail,
    selectedCommOptions,
} from './utils';

export const useEmailSubscriptions = (client) => {
    const [notification, setNotification] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);
    const [addModal, setAddModal] = useState(null);

    const clientId = client?.client_id;

    const {
        data: commsData,
        loading: commsLoading,
        error: commsError,
    } = useQuery(QUERY_ALL_COMMS, { onError: captureException });

    const commSections = useMemo(() => buildCommSections(commsData?.email_communications), [commsData]);

    const commsById = useMemo(() => {
        const byId = new Map();
        for (const { options } of commSections) {
            for (const comm of options) byId.set(comm.communication_id, comm);
        }
        return byId;
    }, [commSections]);

    const commOptions = useMemo(() => flattenCommSections(commSections), [commSections]);

    // `selected` is resolved here rather than in the row so the card and its delete confirmation
    // agree on what the address is subscribed to.
    const emailGroups = useMemo(
        () =>
            groupCommunicationsByEmail(client?.email_subscriptions).map((group) => ({
                ...group,
                selected: selectedCommOptions(group.communicationIds, commsById),
            })),
        [client, commsById]
    );

    // These rows have no id and no type policy in cache.js, so Apollo stores them inline under
    // the client rather than normalizing them — cache.identify on one returns undefined. Refetching
    // rewrites the field on the normalized `clients` entity, which the account page is watching.
    const refetchSubscriptions = useMemo(
        () =>
            clientId
                ? {
                      refetchQueries: [{ query: QUERY_CLIENT_EMAIL_SUBSCRIPTIONS, variables: { client_id: clientId } }],
                      awaitRefetchQueries: true,
                  }
                : {},
        [clientId]
    );

    const onMutationError = useCallback((message) => {
        return (err) => {
            captureException(err);
            setNotification({ severity: 'error', message });
        };
    }, []);

    const [addEmailsToSubscription, { loading: addLoading }] = useMutation(ADD_EMAILS_TO_SUBSCRIPTION, {
        ...refetchSubscriptions,
        onCompleted: () => {
            setNotification({ severity: 'success', message: 'Subscription updated' });
            setAddModal(null);
        },
        onError: onMutationError('Error updating subscription'),
    });

    const [syncEmailCommunications, { loading: syncLoading }] = useMutation(SYNC_EMAIL_COMMUNICATIONS, {
        ...refetchSubscriptions,
        onCompleted: () => {
            setNotification({ severity: 'success', message: 'Subscription updated' });
        },
        onError: onMutationError('Error updating subscription'),
    });

    const [deleteAllForEmail, { loading: deleteLoading }] = useMutation(DELETE_ALL_FOR_EMAIL, {
        ...refetchSubscriptions,
        onCompleted: () => {
            setNotification({ severity: 'success', message: 'Email subscription deleted' });
            setDeleteModal(null);
        },
        onError: onMutationError('Error deleting email subscription'),
    });

    const loading = addLoading || syncLoading || deleteLoading;

    const handleUpdateCommunications = useCallback(
        (group, nextComms) => {
            // Clearing the last chip is the only way to reach zero, and an address subscribed to
            // nothing has no reason to exist — confirm removing it instead of storing an empty card.
            if (!nextComms.length) {
                setDeleteModal(group);
                return;
            }

            const { remove, add } = diffVisibleCommunications(
                group.communicationIds,
                nextComms.map((comm) => comm.communication_id),
                commsById
            );

            if (!remove.length && !add.length) return;

            syncEmailCommunications({
                variables: {
                    client_id: clientId,
                    email: group.email,
                    remove,
                    add: add.map((communication_id) => ({
                        client_id: clientId,
                        communication_id,
                        email: group.email,
                    })),
                },
            });
        },
        [clientId, commsById, syncEmailCommunications]
    );

    const handleDelete = useCallback(
        (group) => {
            deleteAllForEmail({ variables: { client_id: clientId, email: group.email } });
        },
        [clientId, deleteAllForEmail]
    );

    const openAddModal = useCallback(() => {
        setAddModal({ selectedCommunicationIds: [], emails: [] });
    }, []);

    const handleAddEmailSubscription = useCallback(() => {
        const { emails, invalid } = normalizeEmails(addModal?.emails);
        const communicationIds = addModal?.selectedCommunicationIds || [];

        if (invalid.length) {
            setNotification({ severity: 'error', message: `Not a valid email address: ${invalid.join(', ')}` });
            return;
        }

        if (!emails.length) {
            setNotification({ severity: 'error', message: 'Add at least one email address.' });
            return;
        }

        if (!communicationIds.length) {
            setNotification({ severity: 'error', message: 'Choose at least one notification to subscribe to.' });
            return;
        }

        addEmailsToSubscription({
            variables: {
                objects: communicationIds.flatMap((communication_id) =>
                    emails.map((email) => ({ client_id: clientId, communication_id, email }))
                ),
            },
        });
    }, [addModal, clientId, addEmailsToSubscription]);

    return {
        notification,
        setNotification,
        addModal,
        setAddModal,
        deleteModal,
        setDeleteModal,
        commSections,
        commOptions,
        commsLoading,
        commsError,
        emailGroups,
        loading,
        openAddModal,
        handleUpdateCommunications,
        handleDelete,
        handleAddEmailSubscription,
    };
};
